import type { TaskStatus } from '@dyspatch/core';
import { type HoldableTask, holdNeedsAPerson } from '@/lib/fleet';

/**
 * Core's `DEPENDENCY_SATISFIED_STATUSES` (state-machine.ts), mirrored for the
 * same reason `HELD_STATUSES` is in `fleet.ts`: importing a core value pulls
 * `node:crypto` into the browser bundle. Pinned to core by a test in
 * `packages/hub/test/`.
 *
 * `review`, not `done`: a step unblocks the moment its dependency has an open
 * PR, so the graph does not serialise the whole plan behind a person merging
 * one PR at a time (docs/JOB-LIFECYCLE.md 2.10).
 */
export const DEPENDENCY_SATISFIED_STATUSES: readonly TaskStatus[] = ['review', 'done'];

/**
 * Which of a step's declared dependencies have not reached a status that
 * unblocks it. Mirrors core's `unmetDependencies` over the list above, so a key
 * naming a step that does not exist on this job counts as unmet rather than
 * silently dropping out - the same way a dangling edge counts in
 * `dispatchableTasks`'s own SQL.
 */
export function unmetDependencies(
  dependsOn: readonly string[],
  statusByKey: ReadonlyMap<string, TaskStatus>,
): string[] {
  return dependsOn.filter((key) => {
    const status = statusByKey.get(key);
    return status === undefined || !DEPENDENCY_SATISFIED_STATUSES.includes(status);
  });
}

/**
 * A sibling step, as far as this module needs to know it: enough to say
 * whether it is itself stuck. `status` is nullable because a graph node
 * (`GraphTask`) carries no status before Go, when nothing has dependencies
 * worth asking about anyway.
 */
interface Dependency extends Omit<HoldableTask, 'status'> {
  status?: TaskStatus | null;
}

/**
 * Whether an unmet dependency is itself going nowhere without a person: it
 * failed outright, or it is queued and stuck on the one hold only a requeue
 * clears (`holdNeedsAPerson`). Every other unmet dependency is simply still in
 * flight - offered, running, in review - which is the difference between a
 * step reading "waiting on T1" (T1 is working) and one reading "blocked, T1 is
 * held" (T1 is not moving until somebody acts, and neither is this one).
 */
function isStuck(dep: Dependency | undefined): boolean {
  if (!dep) return false;
  const status = dep.status ?? 'draft';
  if (status === 'failed') return true;
  return holdNeedsAPerson({ status, holdReason: dep.holdReason, pausedAt: dep.pausedAt });
}

/** "failed" or "is held": the two ways a dependency can be stuck, in the row's words. */
function stuckWord(dep: Dependency): string {
  return (dep.status ?? 'draft') === 'failed' ? 'failed' : 'is held';
}

/** A queued step's dependency state, in the words a row prints it in. */
export interface DependencyNote {
  text: string;
  /** True for "stuck one edge upstream", so a caller can give it the same
   * emphasis a step's own held-on-a-person state gets, without re-parsing
   * `text` to find out which shape it is. */
  blocked: boolean;
}

/**
 * What a queued step's dependency state reads as, or null when there is
 * nothing to say: the step has no dependencies, or every one of them has
 * already unblocked it.
 *
 * Two shapes, and the difference is the point of issue #11. A step naming
 * dependencies still in flight is waiting its turn, same as a step with
 * nothing to depend on that the scheduler simply has not reached yet. A step
 * naming a dependency that has itself failed, or is held on the one hold only
 * a person clears, is not waiting its turn at all - it is stuck one edge
 * upstream, and saying so, and naming which one, is what tells a reader where
 * to go act rather than where to wait.
 */
export function dependencyNote(
  task: { status?: TaskStatus | null; dependsOn: readonly string[] },
  siblingsByKey: ReadonlyMap<string, Dependency>,
): DependencyNote | null {
  if ((task.status ?? 'draft') !== 'queued' || task.dependsOn.length === 0) return null;

  const statusByKey = new Map<string, TaskStatus>();
  for (const [key, sibling] of siblingsByKey) statusByKey.set(key, sibling.status ?? 'draft');

  const unmet = unmetDependencies(task.dependsOn, statusByKey);
  if (unmet.length === 0) return null;

  const stuck = unmet.filter((key) => isStuck(siblingsByKey.get(key)));
  if (stuck.length > 0) {
    const text = `blocked, ${stuck
      .map((key) => `${key} ${stuckWord(siblingsByKey.get(key)!)}`)
      .join(', ')}`;
    return { text, blocked: true };
  }
  return { text: `waiting on ${unmet.join(', ')}`, blocked: false };
}
